import React from "react";
import { FaCheckCircle, FaTimesCircle, FaInfoCircle, FaExclamationTriangle, FaExchangeAlt, FaEnvelope, FaCalendarAlt } from "react-icons/fa";

export default function ReturnPolicy() {
  return (
    <div className="mt-8 py-12 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h1 className="text-3xl font-bold text-[#D4AF37] mb-8">Return Policy</h1>

        {/* Section 1: General Terms */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaInfoCircle className="mr-2 text-[#D4AF37]" /> General Terms
          </h3>
          <p className="text-sm mb-2">
            At MASHO Perfumes, we strive to ensure customer satisfaction. However, we must enforce certain restrictions on returns, and cancellations to meet business, legal, and contractual obligations.
          </p>
          <p className="text-sm">
            Guidelines specific to certain brands may vary. Please review the applicable guidelines before making a purchase.
          </p>
        </div>

        {/* Section 2: Return/Exchange Eligibility Period */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaCalendarAlt className="mr-2 text-[#D4AF37]" /> Return Timeline
          </h3>
          <ul className="list-disc pl-6 text-sm space-y-2">
            <li>We offer return within the first 7 days from the date of your purchase.</li>
            <li>If 7 days have passed since your purchase, you will not be offered a return of any kind.</li>
            <li>Disputes must be registered within 3 days of receiving the product. After approval, you must send the product back within 7 days.</li>
          </ul>
        </div>

        {/* Section 3: What Can Be Returned */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaCheckCircle className="mr-2 text-[#D4AF37]" /> What Can Be Returned?
          </h3>
          <ul className="list-disc pl-6 text-sm space-y-2">
            <li>Faulty or defective products (e.g., damaged attar bottles, non-working burners).</li>
            <li>Products different from what you ordered.</li>
            <li>Products that look different from their description.</li>
            <li>Products that do not match the size you ordered.</li>
            <li>If you are unsatisfied with the MASHO Perfumes range of attars.</li>
          </ul>
          <p className="text-sm mt-4">
            <strong>Note:</strong> For faulty delivery complaints (e.g., stolen, damaged, or empty packages), an unboxing video is mandatory for your query to be entertained.
          </p>
        </div>

        {/* Section 4: What Cannot Be Returned */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaTimesCircle className="mr-2 text-[#D4AF37]" /> What Cannot Be Returned?
          </h3>
          <ul className="list-disc pl-6 text-sm space-y-2">
            <li>Items where the seal has been broken (unless compensation is provided).</li>
            <li>Custom-made products (e.g., Gifts, Custom mixed perfumes).</li>
            <li>Products purchased during sales or discounts.</li>
            <li>Certain categories of products that are exempted from returns (these will be identified at the time of purchase).</li>
          </ul>
        </div>

        {/* Section 5: Conditions for Returning Products */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaExclamationTriangle className="mr-2 text-[#D4AF37]" /> Conditions for Returning Products
          </h3>
          <ul className="list-disc pl-6 text-sm space-y-2">
            <li>Products must be unused and in the same condition as you received them.</li>
            <li>Products must be returned in their original condition, along with bills, tags, and labels.</li>
            <li>Items must have original packaging intact.</li>
            <li>Products must be unsoiled and in their original form.</li>
            <li>Brand packaging should remain intact.</li>
            <li>Replacement availability depends on stock at the time of the return request.</li>
            <li>Items purchased on sale may not be eligible for return.</li>
          </ul>
        </div>

        {/* Section 6: Return/Exchange Process */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaExchangeAlt className="mr-2 text-[#D4AF37]" /> Return Process
          </h3>
          <ol className="list-decimal pl-6 text-sm space-y-2">
            <li>To initiate a return for an eligible product, send an email to <span className="text-[#D4AF37]">returns@mashoperfumes.com</span>.</li>
            <li>Include your order number, product details, and reason for return in your email.</li>
            <li>Once your request is reviewed, you will receive instructions on how to return the item.</li>
            <li>After we receive your returned product, it will be inspected.</li>
            <li>We will send you an email to notify you about the receipt of your returned product.</li>
            <li>If approved after quality check, your return will be processed according to our policies.</li>
          </ol>
        </div>

        {/* Section 7: Refund Eligibility */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaInfoCircle className="mr-2 text-[#D4AF37]" /> Raising a Dispute
          </h3>
          <p className="text-sm mb-2">
            To raise a dispute, contact us via WhatsApp at +91 70259 59481. Your message must include:
          </p>
          <ul className="list-disc pl-6 text-sm space-y-2">
            <li>Name/image of the product.</li>
            <li>Order ID.</li>
            <li>A detailed description of the issue.</li>
          </ul>
        </div>

        {/* Section 8: Cancellation Policy */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaExclamationTriangle className="mr-2 text-[#D4AF37]" /> Cancellation Policy
          </h3>
          <ul className="list-disc pl-6 text-sm space-y-2">
            <li>You cannot cancel orders after 12 hours.</li>
            <li>Customized orders (e.g., jubbah, handmade caps, abaya, bisht) cannot be canceled.</li>
            <li>If the product has not been dispatched, you can request cancellation.</li>
          </ul>
          <p className="text-sm mt-2">
            Note: MASHO Perfumes reserves the right to cancel any order due to pricing errors or stock unavailability.
          </p>
        </div>
      </div>
    </div>
  );
}